/**
 * LemonSqueezy client library
 *
 * Handles:
 * - Creating checkout URLs
 * - Managing subscriptions
 * - Verifying webhooks
 */

/**
 * LemonSqueezy configuration from environment variables
 */
export const lemonSqueezyConfig = {
  apiKey: import.meta.env.LEMONSQUEEZY_API_KEY || '',
  storeId: import.meta.env.LEMONSQUEEZY_STORE_ID || '',
  monthlyVariantId: import.meta.env.VITE_LEMONSQUEEZY_MONTHLY_VARIANT_ID || '',
  yearlyVariantId: import.meta.env.VITE_LEMONSQUEEZY_YEARLY_VARIANT_ID || '',
};

/**
 * Check if LemonSqueezy is properly configured
 */
export function isLemonSqueezyConfigured(): boolean {
  return Boolean(
    lemonSqueezyConfig.monthlyVariantId &&
    lemonSqueezyConfig.yearlyVariantId
  );
}

/**
 * Get variant ID for a plan type
 */
export function getVariantId(plan: 'monthly' | 'yearly'): string {
  return plan === 'monthly'
    ? lemonSqueezyConfig.monthlyVariantId
    : lemonSqueezyConfig.yearlyVariantId;
}

/**
 * Create a checkout URL for a specific plan
 * This will be called from the frontend and will call our Supabase function
 */
export async function createCheckoutUrl(
  plan: 'monthly' | 'yearly',
  userEmail: string,
  userId: string,
  authToken: string
): Promise<string> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

  if (!supabaseUrl) {
    throw new Error('VITE_SUPABASE_URL is not configured');
  }

  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!anonKey) {
    throw new Error('VITE_SUPABASE_ANON_KEY is not configured');
  }

  console.log('[createCheckoutUrl] Calling Supabase function:', {
    url: `${supabaseUrl}/functions/v1/create-lemonsqueezy-checkout`,
    plan,
    hasAuthToken: !!authToken,
    authTokenLength: authToken?.length
  });

  const response = await fetch(`${supabaseUrl}/functions/v1/create-lemonsqueezy-checkout`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authToken}`,
      'apikey': anonKey
    },
    body: JSON.stringify({ plan, userEmail, userId })
  });

  if (!response.ok) {
    const error = await response.json();
    console.error('[createCheckoutUrl] Error response:', {
      status: response.status,
      statusText: response.statusText,
      error
    });
    throw new Error(error.message || error.error || `Failed to create checkout (${response.status})`);
  }

  const data = await response.json();
  console.log('[createCheckoutUrl] ✅ Success:', { checkoutUrl: data.checkoutUrl });
  return data.checkoutUrl;
}